<template>
  <q-page padding class="bg-grey-2">
    <div class="row justify-between items-center q-mb-md">
      <h4 class="text-h5 q-my-none text-weight-bold">My Games</h4>
      <q-btn color="primary" icon="add" label="New Game" unelevated @click="startNewGame" />
    </div>

    <q-card v-if="savedGames.length === 0" flat bordered class="q-pa-xl text-center text-grey-7">
      <q-icon name="sports_baseball" size="4rem" class="q-mb-md" />
      <div class="text-h6">No saved games found.</div>
      <div>Start a new game to begin charting.</div>
    </q-card>

    <q-list v-else bordered separator class="rounded-borders bg-white">
      <q-item v-for="game in savedGames" :key="game.gameId" v-ripple class="q-py-md">
        <q-item-section>
          <q-item-label class="text-h6">
            {{ game.teamVisiting?.name || 'Away' }} @ {{ game.teamHome?.name || 'Home' }}
          </q-item-label>
          <q-item-label caption>
            Score: {{ getRunTotal(game.visitingScoreByInning) }} -
            {{ getRunTotal(game.homeScoreByInning) }}
          </q-item-label>
        </q-item-section>

        <q-item-section side>
          <div class="text-grey-8 q-gutter-sm row items-center">
            <q-btn round flat icon="download" color="secondary" @click="handleExport(game)">
              <q-tooltip>Export CSV</q-tooltip>
            </q-btn>

            <q-btn
              round
              unelevated
              icon="play_arrow"
              color="primary"
              @click="handleResume(game.gameId)"
            >
              <q-tooltip>Resume Game</q-tooltip>
            </q-btn>

            <!-- New Delete Button -->
            <q-btn
              round
              flat
              icon="delete"
              color="negative"
              @click="confirmDelete(game)"
            >
              <q-tooltip>Delete Game</q-tooltip>
            </q-btn>
          </div>
        </q-item-section>
      </q-item>
    </q-list>

    <!-- BOTTOM SHEET: Confirm Deletion -->
    <q-dialog v-model="showDeleteSheet" position="bottom">
      <q-card class="q-pa-sm" style="border-top-left-radius: 16px; border-top-right-radius: 16px;">
        <q-card-section class="row items-center justify-between q-pb-none">
          <div class="text-h6 text-negative text-weight-bold">Delete Game?</div>
          <q-btn icon="close" flat round dense v-close-popup />
        </q-card-section>

        <q-card-section class="text-grey-8">
          Are you sure you want to permanently delete this game and all of its recorded pitches, plays, and statistics?
          <br><br>
          <strong>This action cannot be undone.</strong>
        </q-card-section>

        <q-card-actions align="right" class="q-pt-md">
          <q-btn flat label="Cancel" color="grey-8" v-close-popup />
          <q-btn unelevated label="Delete Forever" color="negative" @click="executeDelete" :loading="isDeleting" />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </q-page>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useQuasar } from 'quasar'
import { db } from 'src/models/db'
import { useGameStore } from 'src/stores/game-store'
import { exportGameToCSV } from 'src/helpers/helpers'
import type { GameMetadata } from 'src/stores/base-types'

const router = useRouter()
const gameStore = useGameStore()
const $q = useQuasar()

// Reactive list of games for the UI
const savedGames = ref<GameMetadata[]>([])

// State for Delete Bottom Sheet
const showDeleteSheet = ref(false)
const gameToDelete = ref<string | null>(null)
const isDeleting = ref(false)

onMounted(async () => {
  await loadGames()
})

async function loadGames() {
  // Load all metadata shells from Dexie when the page opens.
  // We use reverse() so the most recently created games are at the top.
  const games = await db.games.toArray()
  savedGames.value = games.reverse()
}

// Helper to quickly sum up arrays like [0, 2, 1] for the scoreboard preview
const getRunTotal = (innings: number[]): number => {
  if (!innings) return 0
  return innings.reduce((sum, runs) => sum + runs, 0)
}

const handleResume = async (gameId: string) => {
  // Load the game data into Pinia
  const result = await gameStore.loadGameFromStorage(gameId)

  if (result.status === 'success') {
    // Check if the setup was ever finished
    if (gameStore.activeGameSummary?.isSetupComplete) {
      // The game has started, go to the pitch tracker
      await router.push({ name: 'GameTracker' }) // Or 'GameTracker' if that is your primary active route name
    } else {
      // It's a draft! Send them back to the Setup Wizard to finish their lineups
      await router.push({ name: 'GameSetup' }) // Replace with 'GameSetup' depending on your routing setup
    }
  } else {
    console.error('Failed to load game')
  }
}

const handleExport = async (game: GameMetadata) => {
  // Generate a nice filename like "Away_vs_Home_Inning_3"
  /*
  const awayName = game.teamVisiting?.name || 'Away'
  const homeName = game.teamHome?.name || 'Home'
  const label = `${awayName}_vs_${homeName}`
*/
  await exportGameToCSV(game.gameId)
}

// --- DELETION LOGIC ---
const confirmDelete = (game: GameMetadata) => {
  gameToDelete.value = game.gameId
  showDeleteSheet.value = true
}

const executeDelete = async () => {
  if (!gameToDelete.value) return

  isDeleting.value = true
  try {
    // Cascade delete through our new store action
    await gameStore.deleteGame(gameToDelete.value)

    // Refresh the UI list
    await loadGames()

    $q.notify({
      type: 'positive',
      message: 'Game deleted successfully.',
      position: 'top'
    })
  } catch (error) {
    console.error("Deletion failed:", error)
    $q.notify({
      type: 'negative',
      message: 'Failed to delete game.',
      position: 'top'
    })
  } finally {
    isDeleting.value = false
    showDeleteSheet.value = false
    gameToDelete.value = null
  }
}

const startNewGame = async () => {
  // Clear any active memory so we start fresh
  gameStore.clearActiveTeams()
  // Send them to the roster/team selection setup wizard
  await router.push({ name: 'GameSetup' }) // Ensure this matches your actual route name
}
</script>
